import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import { useAuth } from '@/context/AuthContext';
import { toast } from 'sonner';
import axios from 'axios';
import { RefreshCw, Calendar, Package, Truck, Store } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SubscribeDialog = ({ product, open, onOpenChange, onSuccess }) => {
  const { token } = useAuth();
  const [loading, setLoading] = useState(false);
  
  // Get tomorrow's date as minimum start date
  const getTomorrowDate = () => {
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);
    return tomorrow.toISOString().split('T')[0];
  };
  
  const [formData, setFormData] = useState({
    quantity: 1,
    frequency: 'daily',
    delivery_type: 'delivery',
    delivery_address: '',
    start_date: getTomorrowDate(),
    end_date: '',
    delivery_days: []
  });

  const weekDays = [
    { id: 'monday', label: 'Mon' },
    { id: 'tuesday', label: 'Tue' },
    { id: 'wednesday', label: 'Wed' },
    { id: 'thursday', label: 'Thu' },
    { id: 'friday', label: 'Fri' },
    { id: 'saturday', label: 'Sat' }
  ];

  const handleDayToggle = (day) => {
    setFormData(prev => ({
      ...prev,
      delivery_days: prev.delivery_days.includes(day)
        ? prev.delivery_days.filter(d => d !== day)
        : [...prev.delivery_days, day]
    }));
  };

  const handleSubmit = async () => {
    if (!formData.end_date) {
      toast.error('Please select an end date');
      return;
    }
    if (formData.delivery_type === 'delivery' && !formData.delivery_address) {
      toast.error('Please enter delivery address');
      return;
    }
    if (formData.frequency === 'weekly' && formData.delivery_days.length === 0) {
      toast.error('Please select at least one delivery day');
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post(
        `${API}/subscriptions/create`,
        {
          product_id: product.id,
          quantity: formData.quantity,
          frequency: formData.frequency,
          delivery_type: formData.delivery_type,
          delivery_address: formData.delivery_address,
          start_date: formData.start_date,
          end_date: formData.end_date,
          delivery_days: formData.frequency === 'weekly' ? formData.delivery_days : null
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      toast.success(`Subscription created! ${response.data.total_deliveries} deliveries scheduled.`);
      onOpenChange(false);
      onSuccess?.();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create subscription');
    } finally {
      setLoading(false);
    }
  };

  const calculateEstimate = () => {
    const price = (product?.price || 0) * formData.quantity;
    let deliveries = 0;
    
    if (formData.start_date && formData.end_date) {
      const start = new Date(formData.start_date);
      const end = new Date(formData.end_date);
      const days = Math.ceil((end - start) / (1000 * 60 * 60 * 24));
      
      if (formData.frequency === 'daily') deliveries = Math.floor(days * 6 / 7); // Exclude Sundays
      else if (formData.frequency === 'weekly') deliveries = Math.ceil(days / 7) * (formData.delivery_days.length || 1);
      else if (formData.frequency === 'biweekly') deliveries = Math.ceil(days / 14);
      else if (formData.frequency === 'monthly') deliveries = Math.ceil(days / 30);
    }

    return { perDelivery: price, total: price * deliveries, deliveries };
  };

  const estimate = calculateEstimate();

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <RefreshCw className="h-5 w-5 text-primary" />
            Subscribe to {product?.name}
          </DialogTitle>
          <DialogDescription>
            Get this product delivered regularly at your preferred frequency
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          {/* Product Summary */}
          <div className="flex items-center gap-3 p-3 bg-amber-50 rounded-lg">
            <Package className="h-10 w-10 text-primary" />
            <div>
              <p className="font-semibold">{product?.name}</p>
              <p className="text-sm text-primary">₹{product?.price} each</p>
            </div>
          </div>

          {/* Quantity */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>Quantity per Delivery</Label>
              <Input
                type="number"
                value={formData.quantity}
                onChange={(e) => setFormData({ ...formData, quantity: parseInt(e.target.value) || 1 })}
                min="1"
                className="mt-1"
              />
            </div>
            <div>
              <Label>Frequency</Label>
              <Select
                value={formData.frequency}
                onValueChange={(value) => setFormData({ ...formData, frequency: value })}
              >
                <SelectTrigger className="mt-1">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="daily">Daily (Mon-Sat)</SelectItem>
                  <SelectItem value="weekly">Weekly</SelectItem>
                  <SelectItem value="biweekly">Every 2 Weeks</SelectItem>
                  <SelectItem value="monthly">Monthly</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Weekly Day Selection */}
          {formData.frequency === 'weekly' && (
            <div>
              <Label>Delivery Days</Label>
              <div className="flex flex-wrap gap-2 mt-2">
                {weekDays.map((day) => (
                  <button
                    key={day.id}
                    type="button"
                    onClick={() => handleDayToggle(day.id)}
                    className={`px-3 py-1.5 rounded-full text-sm font-medium transition-all ${
                      formData.delivery_days.includes(day.id)
                        ? 'bg-primary text-white'
                        : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                    }`}
                  >
                    {day.label}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Dates */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>Start Date</Label>
              <Input
                type="date"
                value={formData.start_date}
                onChange={(e) => setFormData({ ...formData, start_date: e.target.value })}
                min={getTomorrowDate()}
                className="mt-1"
              />
              <p className="text-xs text-muted-foreground mt-1">Starts from tomorrow</p>
            </div>
            <div>
              <Label>End Date</Label>
              <Input
                type="date"
                value={formData.end_date}
                onChange={(e) => setFormData({ ...formData, end_date: e.target.value })}
                min={formData.start_date}
                className="mt-1"
              />
            </div>
          </div>

          {/* Delivery Type */}
          <div>
            <Label>Delivery Type</Label>
            <div className="flex gap-3 mt-2">
              <button
                type="button"
                onClick={() => setFormData({ ...formData, delivery_type: 'delivery' })}
                className={`flex-1 p-3 rounded-lg border-2 transition-all flex items-center justify-center gap-2 ${
                  formData.delivery_type === 'delivery'
                    ? 'border-primary bg-primary/5'
                    : 'border-gray-200'
                }`}
              >
                <Truck className="h-4 w-4" />
                Delivery
              </button>
              <button
                type="button"
                onClick={() => setFormData({ ...formData, delivery_type: 'pickup' })}
                className={`flex-1 p-3 rounded-lg border-2 transition-all flex items-center justify-center gap-2 ${
                  formData.delivery_type === 'pickup'
                    ? 'border-primary bg-primary/5'
                    : 'border-gray-200'
                }`}
              >
                <Store className="h-4 w-4" />
                Pickup
              </button>
            </div>
          </div>

          {/* Delivery Address */}
          {formData.delivery_type === 'delivery' && (
            <div>
              <Label>Delivery Address</Label>
              <Textarea
                value={formData.delivery_address}
                onChange={(e) => setFormData({ ...formData, delivery_address: e.target.value })}
                placeholder="Enter your complete delivery address"
                rows={2}
                className="mt-1"
              />
            </div>
          )}

          {/* Estimate */}
          <div className="p-4 bg-green-50 rounded-lg border border-green-200">
            <p className="text-sm font-medium text-green-800 mb-2">Subscription Summary</p>
            <div className="grid grid-cols-3 gap-2 text-center">
              <div>
                <p className="text-2xl font-bold text-green-700">{estimate.deliveries}</p>
                <p className="text-xs text-green-600">Deliveries</p>
              </div>
              <div>
                <p className="text-2xl font-bold text-green-700">₹{estimate.perDelivery}</p>
                <p className="text-xs text-green-600">Per Delivery</p>
              </div>
              <div>
                <p className="text-2xl font-bold text-green-700">₹{estimate.total}</p>
                <p className="text-xs text-green-600">Est. Total</p>
              </div>
            </div>
          </div>

          <p className="text-xs text-muted-foreground">
            * Payment link will be sent before each delivery. You can pause or cancel anytime.
          </p>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={handleSubmit} disabled={loading} className="bg-primary">
            {loading ? 'Creating...' : 'Start Subscription'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default SubscribeDialog;
